import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

@Injectable({ providedIn: 'root' })
export class EdgeToEdgeService {
  private observer?: MutationObserver;
  private edgeToEdgePlugin?: typeof import('@capawesome/capacitor-android-edge-to-edge-support').EdgeToEdge;

  isSupported(): boolean {
    return Capacitor.getPlatform() === 'android';
  }

  async enable(): Promise<void> {
    if (!this.isSupported()) return;

    // Load plugin once
    try {
      const mod = await import('@capawesome/capacitor-android-edge-to-edge-support');
      this.edgeToEdgePlugin = mod.EdgeToEdge;
      await this.edgeToEdgePlugin.enable();
    } catch {
      // Plugin not available — StatusBar fallback below still applies top bar color
    }

    await this.applyTheme();
    this.setupThemeWatcher();
  }

  async applyTheme(): Promise<void> {
    if (!this.isSupported()) return;

    const color = this.getPrimaryColor();
    const style = this.isLight(color) ? Style.Light : Style.Dark;

    // Set status bar style (icon color) — works on all Android versions
    await StatusBar.setStyle({ style }).catch(() => {});

    // Set status bar background color — works on Android < 15
    await StatusBar.setBackgroundColor({ color }).catch(() => {});

    // EdgeToEdge plugin: sets the container background visible behind BOTH
    // the status bar AND the navigation bar, and is the only method that
    // works on Android 15+ where StatusBar.setBackgroundColor is a no-op
    if (this.edgeToEdgePlugin) {
      await this.edgeToEdgePlugin.setBackgroundColor({ color }).catch(() => {});
    }
  }

  private getPrimaryColor(): string {
    document.body.offsetHeight; // force style recalc
    let color = getComputedStyle(document.documentElement)
      .getPropertyValue('--ion-color-primary')
      .trim();
    if (color.startsWith('rgb')) color = this.rgbToHex(color);
    color = color.replace(/[^#0-9a-fA-F]/g, '');
    if (color && !color.startsWith('#')) color = '#' + color;
    return color.length === 7 ? color : '#2563eb';
  }

  private isLight(hex: string): boolean {
    const h = hex.replace('#', '');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5;
  }

  private rgbToHex(rgb: string): string {
    const m = rgb.match(/\d+/g);
    if (m && m.length >= 3) {
      return '#' + [m[0], m[1], m[2]]
        .map((n) => parseInt(n).toString(16).padStart(2, '0'))
        .join('');
    }
    return rgb;
  }

  private setupThemeWatcher(): void {
    this.observer?.disconnect();
    // Watch document.documentElement — settings service sets data-color-scheme
    // and data-theme attributes on the <html> element, not body classes
    this.observer = new MutationObserver(() => {
      setTimeout(() => void this.applyTheme(), 50);
    });
    this.observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-color-scheme', 'data-theme'],
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
